import React, { useContext, useEffect, useState } from 'react';
import cn from 'classnames';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import ConfigContext from '../../../contexts/ConfigContext';
import useSearchFilters from '../../../hooks/useSearchFilters';
import { upperFirst } from '../../../modules/helpers';

// Components
import QuoteCta from '../content/QuoteCta';
import FeaturedTrucks from '../FeaturedTrucks';
import SimpleCta from '../content/SimpleCta';
import FeaturedPods from '../FeaturedPods';
import CateringCta from '../content/CateringCta';
import SearchResults from './SearchResults';
import SearchFilters from './SearchFilters';
import AllTrucks from './AllTrucks';
import Meta from '../../shared/Meta';
import { SearchMap } from './Search.imports-loadable';

import styles from './Search.module.postcss';

const sliderSettings = {
  slidesToShow: 5,
  slidesToScroll: 1,
};
const mapOpenSliderSettings = {
  slidesToShow: 3,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 1350,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3
      }
    },
    {
      breakpoint: 1250,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2
      }
    },
    {
      breakpoint: 1100,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2
      }
    },
    {
      breakpoint: 900,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2
      }
    },
    {
      breakpoint: 732,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1
      }
    }
  ]
};

const featuredOptions = { page_size: 5, featured: true };
const featuredPodOptions = { page_size: 5 };

function Search() {
  const { city_name, vehicle_type, site_title } = useContext(ConfigContext);
  const vehicleLowerCase = vehicle_type.toLowerCase();

  const { searchFilters, setFilters, mapOpen, setMapOpen } = useSearchFilters({ sendToSearchPage: false });
  const filterKeys = Object.keys(searchFilters);
  const hasFilters = filterKeys.length > 0;
  const dateKeys = filterKeys.filter(key => ['day', 'open_now', 'tomorrow'].includes(key));
  const hasDateFilters = dateKeys.length > 0;

  const [searchResults, setSearchResults] = useState(null);
  const [hoverLocation, setHoverLocation] = useState(null);

  let title = `Search - ${site_title}`;
  const description = `Find nearby food ${vehicleLowerCase}s. Search by food type, location, day of the week, and more.`;
  if (searchFilters.query) {
    const query = upperFirst(searchFilters.query);
    const updateDate = dayjs().format('MMMM YYYY');
    title = `The Top 10 Best ${query} Food ${vehicle_type}s Near Me - ${updateDate} : Find Nearby ${query} Food ${vehicle_type} Schedule`;
  }

  // Close map and clear bounds when date filters are removed
  useEffect(() => {
    if (!hasDateFilters && mapOpen) {
      setMapOpen(false);
    }

    if ((!mapOpen || !hasDateFilters)
        && (searchFilters.sw_lat || searchFilters.sw_lng || searchFilters.ne_lat || searchFilters.ne_lng)) {
      setFilters({
        sw_lat: null,
        sw_lng: null,
        ne_lat: null,
        ne_lng: null,
      });
    }
  }, [mapOpen, hasDateFilters, setMapOpen, setFilters, searchFilters.sw_lat, searchFilters.sw_lng, searchFilters.ne_lat, searchFilters.ne_lng]);

  return (
    <div className={cn(styles.Search, {
      [styles.Search__mapOpen]: mapOpen,
    })}>
      <Meta
        title={title}
        description={description}
        noindex={!!hasFilters}
      />

      <SearchFilters
        filters={searchFilters}
        setFilters={setFilters}
        mapOpen={mapOpen}
        setMapOpen={setMapOpen}
        noMapToggle={!hasDateFilters}
      />

      <div className={cn(styles.Search_Body, 'Section u-pt05', {
        [styles.Search_Body__mapOpen]: mapOpen,
      })}>
        <div className={styles.Search_Content}>
          <section className='InnerSection u-pt0 u-pb0'>
            <QuoteCta />
          </section>

          <FeaturedTrucks
            title='Sponsored Results'
            className='InnerSection'
            showMenuLinks
            sliderSettings={mapOpen ? mapOpenSliderSettings : sliderSettings}
            options={featuredOptions}
            noLazy
          />

          { !hasDateFilters &&
            <AllTrucks
              page={searchFilters.page}
              query={searchFilters.query}
              foodCategories={searchFilters.food_categories}
            />
          }

          { hasDateFilters &&
            <SearchResults
              setSearchResults={setSearchResults}
              query={searchFilters.query}
              page={searchFilters.page}
              day={searchFilters.day}
              foodCategories={searchFilters.food_categories}
              openNow={searchFilters.open_now}
              tomorrow={searchFilters.tomorrow}
              swLat={searchFilters.sw_lat}
              swLng={searchFilters.sw_lng}
              neLat={searchFilters.ne_lat}
              neLng={searchFilters.ne_lng}
              setHoverLocation={setHoverLocation}
            />
          }

          { city_name === 'Seattle' &&
            <section className='InnerSection--extraPadding'>
              <FeaturedPods
                options={featuredPodOptions}
                sliderSettings={mapOpen ? mapOpenSliderSettings : sliderSettings}
              />
            </section>
          }

          <section className='InnerSection u-pb0'>
            <SimpleCta
              text={`Can't find your food ${vehicleLowerCase}? Add your business for free!`}
              link='/vendor/register'
              linkTitle='Join Now'
            />
          </section>
        </div>

        <div className={styles.Search_Map}>
          { mapOpen &&
            <Sentry.ErrorBoundary fallback={null}>
              <SearchMap
                searchResults={searchResults}
                hoverLocation={hoverLocation}
                setFilters={setFilters}
              />
            </Sentry.ErrorBoundary>
          }
        </div>
      </div>

      <section className='Section--extraPadding'>
        <CateringCta />
      </section>
    </div>
  );
}

export default Search;
